<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import ImageCarousel from '@/components/utilities/ImageCarousel.vue';
import ImageLightbox from '@/components/utilities/ImageLightbox.vue';
import { formatCurrencyWithoutDecimal } from '@/helpers/currencyHelper.ts';
import { formatAvailabilityDate } from '@/helpers/dateHelpers.ts';
import { getStyleThumbnailImageUrl } from '@/helpers/imageHelpers.ts';
import type { LocaleSchema } from '@/locales/index.ts';
import type { Building } from '@/typings/buildings/Building.ts';
import type { QuoteOption } from '@/typings/quotes/QuoteOption.ts';
import type { Unit } from '@/typings/units/Unit.ts';
import type { UnitImage } from '@/typings/units/UnitImage.ts';
import type { UnitStyle } from '@/typings/units/UnitStyle.ts';
import type { UnitStyleImage } from '@/typings/units/UnitStyleImage.ts';
import { getUnitPositionText } from '@/enums/UnitPosition';
import { isUnitDisabled, UnitStatusEnum } from '@/enums/UnitStatus';

const { t, locale } = useI18n<{message: LocaleSchema}>({
  useScope: 'global',
});

const props = defineProps<{
  unit: Unit;
  building: Building;
  unitStyles: UnitStyle[];
  options: QuoteOption[];
}>();

const emit = defineEmits<{
  'update:selectedConfigurationId': [value: number | undefined];
  'update:selectedStyleId': [value: number | undefined];
  'update:selectedOptions': [value: number[]];
  'update:isFirstTimeHomebuyer': [value: boolean];
}>();

const optionsHeading = computed(() => {
  return props.unit.hasDefaultOptions ? `${t('unitSummary.additionalOptions')} (${t('unitSummary.preConfigured_F', 2).toLocaleLowerCase()})` : t('unitSummary.additionalOptions');
});

const filteredStyles = computed(() => {
  // Use unit.unitStyle if available, otherwise fallback to building unitStyles
  let availableStyles: UnitStyle[] = [];
  
  if (props.unit.unitStyles && props.unit.unitStyles.length > 0) {
    // If unit has unitStyles array, use it (already filtered by backend logic)
    availableStyles = props.unit.unitStyles.filter(style => !style.isSystem);
  } else if (props.building.unitStyles && props.building.unitStyles.length > 0) {
    // Fallback to building unit styles, filter out system styles
    availableStyles = props.building.unitStyles.filter(style => !style.isSystem);
  } else {
    // Final fallback to props.unitStyles (legacy support)
    availableStyles = props.unitStyles.filter(style => !style.isSystem);
  }
  
  return availableStyles;
});

const selectedStyleId = ref<number | null>(
  filteredStyles.value.length > 0 ? filteredStyles.value[0].id : null
);

const selectedStyle = computed(() => {
  if (!selectedStyleId.value) return null;
  return filteredStyles.value.find(style => style.id === selectedStyleId.value) || null;
});

const selectedStyleImages = computed(() => {
  if (!selectedStyle.value?.images) return [];
  
  const localeValue = locale.value.toLowerCase();
  
  return [...selectedStyle.value.images]
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
    .map((img: UnitStyleImage, index: number) => ({
      imageUrl: img.imageUrl,
      displayOrder: img.displayOrder,
      alt: `${selectedStyle.value?.localizedName} - Image ${index + 1}`,
      caption: localeValue === 'en-ca' ? img.nameEn : img.name
    }));
});

const selectedOptions = ref<number[]>([]);
const isFirstTimeHomebuyer = ref(false);

const filteredOptions = computed(() => {
  if(props.unit.hasDefaultOptions) {
    return props.options.filter(option => option.quantity > 0);
  }

  return props.options;
});

const description = computed(() => {
  const localeMapping: Record<string, string> = {
    'fr-ca': 'description',
    'en-ca': 'descriptionEn'
  };

  const localeValue = locale.value.toLowerCase();

  return props.unit[localeMapping[localeValue]];
});

const promotion = computed(() => {
  const localeMapping: Record<string, string> = {
    'fr-ca': 'promotion',
    'en-ca': 'promotionEn'
  };

  const localeValue = locale.value.toLowerCase();

  return props.unit[localeMapping[localeValue]];
});

const floorName = computed(() => {
  const localeMapping: Record<string, string> = {
    'fr-ca': props.unit.floorName != null ? props.unit.floorName : t('common.notAvailableAbbreviated'),
    'en-ca': props.unit.floorNameEn != null ? props.unit.floorNameEn : t('common.notAvailableAbbreviated')
  };

  const localeValue = locale.value.toLowerCase();

  return localeMapping[localeValue];
});

const unitImages = computed(() => {
  const images = props.unit.images as UnitImage[] | undefined;
  if (!images?.length) return [];
  
  const localeValue = locale.value.toLowerCase();
  
  return images.map((img: UnitImage) => ({
    imageUrl: img.imageUrl,
    displayOrder: img.displayOrder,
    alt: `Image ${img.displayOrder || 1} - ${t('unitSummary.unit')} ${props.unit.unitNumber}`,
    caption: localeValue === 'en-ca' ? img.nameEn : img.name
  }));
});

const styleCount = computed(() => {
  return filteredStyles.value.length;
});

const getOptionPrice = (option: QuoteOption): string => {
  return formatCurrencyWithoutDecimal(props.unit.hasDefaultOptions ? 0 : option.price, locale);
};

function getStyleThumbnailImage(imageUrl: string): string {
  return getStyleThumbnailImageUrl(imageUrl, 300);
}

const shouldShowAvailabilityDate = computed(() => {
  // Toujours afficher la section dans le contexte purchase
  return true;
});

const humanReadableAvailabilityDate = computed(() => {
  return formatAvailabilityDate(props.unit.availabilityDate, locale.value.toLowerCase(), t, 'purchase', props.unit.status);
});

const unitPositionText = computed(() => getUnitPositionText(props.unit.position, t));

// Configurations disponibles avec logique d'héritage
const availableConfigurations = computed(() => {
  const isEnglish = locale.value.toLowerCase().includes('en');
  const configs: Array<{value: number; label: string; systemKey: string; amount?: number | null}> = [];
  
  if (!props.unit.unitUnitConfigurations || props.unit.unitUnitConfigurations.length === 0) {
    return configs;
  }
  
  props.unit.unitUnitConfigurations.forEach(configItem => {
    const label = isEnglish
      ? (configItem.unitConfigurationNameEn || '')
      : (configItem.unitConfigurationName || '');
    
    if (label) {
      configs.push({
        value: configItem.unitConfigurationId,
        label: label,
        systemKey: configItem.unitConfigurationSystemKey || '',
        amount: configItem.amount
      });
    }
  });
  
  return configs;
});

const selectedConfigurationId = ref<number | null>(
  availableConfigurations.value.length > 0 ? availableConfigurations.value[0].value : null
);

const selectedConfiguration = computed(() => {
  return availableConfigurations.value.find(c => c.value === selectedConfigurationId.value);
});

const floorPlanImage = computed(() => {
  if (!selectedConfiguration.value) return null;
  
  // Utiliser unitUnitConfigurations pour trouver l'image
  const configImage = props.unit.unitUnitConfigurations?.find(
    ci => ci.unitConfigurationId === selectedConfiguration.value?.value
  );
  
  return configImage?.planImageUrl || null;
});

function onStyleChange(event: Event) {
  const target = event.target as HTMLSelectElement;
  selectedStyleId.value = target.value ? parseInt(target.value) : null;
}

function onConfigurationChange(configId: number) {
  selectedConfigurationId.value = configId;
  emit('update:selectedConfigurationId', selectedConfigurationId.value ?? undefined);
}

function onStyleChangeHandler(event: Event) {
  onStyleChange(event);
  emit('update:selectedStyleId', selectedStyleId.value ?? undefined);
}

function onOptionsChange() {
  emit('update:selectedOptions', selectedOptions.value);
}

function onFirstTimeHomebuyerChange() {
  emit('update:isFirstTimeHomebuyer', isFirstTimeHomebuyer.value);
}

// Emit initial values
emit('update:selectedConfigurationId', selectedConfigurationId.value ?? undefined);
emit('update:selectedStyleId', selectedStyleId.value ?? undefined);
emit('update:selectedOptions', selectedOptions.value);
emit('update:isFirstTimeHomebuyer', isFirstTimeHomebuyer.value);
</script>

<template>
  <article class="unitSummary-details">
    <!-- Configuration Selection - Moved to top, only if multiple choices -->
    <section v-if="availableConfigurations.length > 1" class="unitSummary-section">
      <span class="unitSummary-sectionTitle">{{ t('unitSummary.chooseConfiguration') }}</span>
      <div class="unitSummary-configSelector">
        <button
          v-for="config in availableConfigurations"
          :key="config.value"
          class="config-button"
          :class="{ 'config-button_active': selectedConfigurationId === config.value }"
          @click="onConfigurationChange(config.value)"
          type="button"
        >
          {{ config.label }}
          <span v-if="config.amount" class="config-amount-pill">+{{ formatCurrencyWithoutDecimal(config.amount, locale) }}</span>
        </button>
      </div>
    </section>

    <section v-if="unit.status === UnitStatusEnum.Available" class="unitSummary-section">
      <label class="unitSummary-firstTimeHomebuyer">
        <input
          v-model="isFirstTimeHomebuyer"
          type="checkbox"
          @change="onFirstTimeHomebuyerChange"
        />
        <span>{{ t('unitSummary.firstTimeHomebuyer.label') }}</span>
      </label>
      <p class="unitSummary-firstTimeHomebuyerNote">
        {{ t('unitSummary.firstTimeHomebuyer.note') }}
      </p>
      <p class="unitSummary-firstTimeHomebuyerConditions">
        {{ t('unitSummary.firstTimeHomebuyer.conditions') }}
      </p>
    </section>

    <hr>

    <section class="unitSummary-section">
      <p v-if="unit.hasDefaultOptions" class="unitSummary-notice">
        <b>{{ t('unitSummary.preConfiguredNotice') }}</b>
      </p>
      <dl class="unitSummary-gridData preferredColumnCountGrid">
        <div class="unitSummary-gridDataItem">
          <dt class="unitSummary-gridDataLabel">
            {{ t('browsingApp.regions.label') }}
          </dt>
          <dd class="unitSummary-gridDataValue">
            {{ unit.localizedRegionName }}
          </dd>
        </div>

        <div class="unitSummary-gridDataItem">
          <dt class="unitSummary-gridDataLabel">
            {{ t('browsingApp.projects.label') }}
          </dt>
          <dd class="unitSummary-gridDataValue">
            {{ unit.localizedProjectName }}
          </dd>
        </div>

        <div class="unitSummary-gridDataItem">
          <dt class="unitSummary-gridDataLabel">
            {{ t('browsingApp.buildings.buildingNumber') }}
          </dt>
          <dd class="unitSummary-gridDataValue">
            {{ unit.buildingNo }}
          </dd>
        </div>

        <div class="unitSummary-gridDataItem">
          <dt class="unitSummary-gridDataLabel">
            {{ t('browsingApp.buildings.floor') }}
          </dt>
          <dd class="unitSummary-gridDataValue">
            {{ floorName }}
          </dd>
        </div>

        <div class="unitSummary-gridDataItem">
          <dt class="unitSummary-gridDataLabel">
            {{ t('browsingApp.units.unitNumber') }}
          </dt>
          <dd class="unitSummary-gridDataValue">
            {{ unit.unitNumber }}
          </dd>
        </div>

        <div class="unitSummary-gridDataItem">
          <dt class="unitSummary-gridDataLabel">
            {{ t('unitSummary.roomCount') }}
          </dt>
            <dd class="unitSummary-gridDataValue">
            {{ selectedConfiguration?.label || 'n.d.' }}
            <span v-if="selectedConfiguration?.amount" class="config-amount-pill">
              +{{ formatCurrencyWithoutDecimal(selectedConfiguration.amount, locale) }}
            </span>
          </dd>
        </div>
        <!-- Date de disponibilité -->
        <div v-if="shouldShowAvailabilityDate" class="unitSummary-gridDataItem">
          <dt class="unitSummary-gridDataLabel">
            {{ t('unitSummary.availabilityDate') }}
          </dt>
          <dd class="unitSummary-gridDataValue">
            {{ humanReadableAvailabilityDate }}
          </dd>
        </div>
        <!-- Position -->
        <div v-if="unitPositionText" class="unitSummary-gridDataItem">
          <dt class="unitSummary-gridDataLabel">
            {{ t('browsingApp.units.position') }}
          </dt>
          <dd class="unitSummary-gridDataValue">
            {{ unitPositionText }}
          </dd>
        </div>
      </dl>
    </section>

    <template v-if="filteredOptions.length > 0 && unit.hasDefaultOptions">
      <hr>
      
      <section class="unitSummary-section">
        <h2 class="unitSummary-sectionTitle">
          {{ optionsHeading }}
        </h2>
        <dl class="unitSummary-list">
          <div v-for="option in filteredOptions"
               :key="option.optionId"
               class="unitSummary-item">
            <dt class="unitSummary-itemLabel">
              {{ option.localizedOptionName }}
            </dt>
          </div>
        </dl>
      </section>      
    </template>
    <template v-else-if="filteredOptions.length > 0">
      <hr>
      
      <section class="unitSummary-section">
        <h2 class="unitSummary-sectionTitle">
          {{ optionsHeading }}
        </h2>
        <dl class="unitSummary-list">
          <label v-for="option in filteredOptions"
                 :key="option.optionId"
                 class="unitSummary-item unitSummary-item_clickable">
            <span>
              <input
                type="checkbox"
                :value="option.optionId"
                v-model="selectedOptions"
                @change="onOptionsChange"
              />
              <dt class="unitSummary-itemLabel">
                {{ option.localizedOptionName }}
              </dt>
            </span>
            <dd v-if="!isUnitDisabled(unit.status)" class="unitSummary-itemPrice">
              {{ getOptionPrice(option) }}
            </dd>
          </label>
        </dl>
      </section>      
    </template>

    <!-- Unit Images -->
    <template v-if="unitImages && unitImages.length > 0">
      <hr>
      
      <section class="unitSummary-section">
        <h2 class="unitSummary-sectionTitle">
          {{ $t('unitSummary.images') }}
        </h2>
        <ImageCarousel :images="unitImages"
                       :show-lightbox="true"
                       :show-dots="true"
                       :show-arrows="true"
                       aspect-ratio="16/9"
                       :image-width="600" />
      </section>
    </template>
    <template v-if="description">
      <hr>
      <section class="unitSummary-section">
        <h2 class="unitSummary-sectionTitle">
          {{ $t('unitSummary.description') }}
        </h2>
        <div class="description" v-html="description"></div>
      </section>
    </template>

    
    <template v-if="filteredStyles.length > 0">
      <hr>
      <section class="unitSummary-section">
        <h2 class="unitSummary-sectionTitle">
          {{ $t('unitSummary.style') }}
        </h2>
        <!-- Dropdown de sélection de style -->
        <div class="unitSummary-styleSelector" v-if="filteredStyles.length > 1">
          <select :value="selectedStyleId"
                  @change="onStyleChangeHandler"
                  class="unitSummary-styleSelect">
            <option v-for="style in filteredStyles"
                    :key="style.id"
                    :value="style.id">
              {{ style.localizedName }}
            </option>
          </select>
        </div>
        <!-- Titre du style sélectionné si un seul style -->
        <div v-else-if="selectedStyle" class="unitSummary-selectedStyleTitle">
          <span>{{ selectedStyle.localizedName }}</span>
        </div>
        <!-- Grille d'images du style sélectionné -->
        <div v-if="selectedStyleImages.length > 0" class="unitSummary-styleImagesGrid">
          <div v-for="image in selectedStyleImages"
              :key="image.displayOrder"
              class="unitSummary-styleImageItem">
            <figure class="unitSummary-styleImageFigure">
              <ImageLightbox :display-url="getStyleThumbnailImage(image.imageUrl)"
                            :modal-url="image.imageUrl"
                            :alt="image.alt" />
              <figcaption v-if="image.caption && false"
                          class="unitSummary-styleImageCaption">
                {{ image.caption }}
              </figcaption>
            </figure>
          </div>
        </div>
      </section>
    </template>
  </article>
</template>

<style lang="scss">
@import '@/assets/styles/components/unitSummaryDetails.scss';

/* Styles spécifiques au composant Purchase */
.unitSummary-details {
  --flowSpacing: var(--spc48-24);
  
  > * + * {
    margin-top: 2.4rem;
  }

  hr {
    height: var(--contentSizeBorderWidth);
    border: none;
    background-color: var(--contentColorBorder);
  }
}

/* Styles spécifiques Purchase - Header */
.unitSummary-header {
  > * + * {
    margin-top: 12px;
  }
}

.unitSummary-headerTitle {
  margin-bottom: 0;
  font-size: var(--fs34-22);
  font-weight: 700;
}

.unitSummary-headerSubtitle {
  font-size: var(--fs20-16);
}

/* Styles spécifiques Purchase - Sections */
.unitSummary-section {
  font-weight: 700;

  > * + * {
    margin-top: 8px;
  }
  .description{
    font-weight: 400;
  }
}

.unitSummary-sectionTitle {
  font-family: Inter;
  font-size: .9em;
  font-weight: 700;
  color: var(--gray-500);
}

.unitSummary-notice {
  margin-top: 0;
  margin-bottom: 2rem;
  border-radius: 4px;
  padding: 1.2rem;
  background-color: var(--gray-50);
  font-size: 1.4rem;
  font-weight: 400;
  color: var(--gray-600);
  line-height: 1.5;
}

.unitSummary-firstTimeHomebuyer {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  cursor: pointer;
  font-size: 1.6rem;
  font-weight: 700;

  input[type="checkbox"] {
    cursor: pointer;
  }
}

.unitSummary-firstTimeHomebuyerNote {
  margin-top: 0.4rem;
  margin-left: calc(1.3rem + 1.2rem);
  font-size: 1.4rem;
  font-weight: 400;
  color: var(--gray-600);
}

.unitSummary-firstTimeHomebuyerConditions {
  margin-top: 0.2rem;
  margin-left: calc(1.3rem + 1.2rem);
  font-size: 1.2rem;
  font-weight: 400;
  color: var(--gray-500);
}

/* Styles spécifiques Purchase - Grid Data */
.unitSummary-gridData {
  --column-count: 2;

  dt {
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--gray-500);
  }

  dd {
    margin-top: 0;
    font-size: 1.6rem;
    font-weight: 700;
  }
}

/* Styles spécifiques Purchase - Lists */
.unitSummary-list {
  list-style: none;
  padding: 0;
  margin-inline: 0;
  
  li {
    padding: 0;
    margin-bottom: 0;
    
    &::before {
      content: none;
    }
  }

  > * + * {
    margin-top: 8px;
  }
  
  &_styleImageList {
    --min-card-size: 200px;
    align-items: start;
    width: 100%;

    &:has(> :only-child) {
      --column-count: 2;
    }

    > * + * {
      margin-top: 0;
    }
  }
}

/* Styles spécifiques Purchase - Items */
.unitSummary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.6rem;
  padding: 8px 12px;

  > span{
    display:flex;
    align-items: center;
    gap: 1.6rem;
  }

  &:nth-child(even) {
    background-color: var(--gray-50);
    border-radius: 4px;
  }

  &_clickable {
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: var(--gray-100);
      border-radius: 4px;
    }

    &:nth-child(even):hover {
      background-color: var(--gray-150, var(--gray-100));
    }

    input[type="checkbox"] {
      margin-right: 8px;
      cursor: pointer;
    }
  }
}

.unitSummary-itemLabel {
  flex: 1;
}

.unitSummary-itemPrice {
  min-width: 12ch;
  margin-top: 0;
  font-weight: 500;
  text-align: right;
}

.unitSummary-styleImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Styles spécifiques Purchase - Media Queries */
@media (width <= 600px) {
  .unitSummary-styleSection {
    display: none;
  }
}
@media (width <= 430px) {
  .unitSummary-item {
    display: block;
  }
}

.config-amount-pill {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  margin-left: 0.5rem;
  background-color: #22c55e;
  color: white;
  font-size: 0.85em;
  font-weight: 600;
  border-radius: 12px;
  white-space: nowrap;
}
</style>

